import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/work-permit/type/")({
  loader: async () => {
    const { WP_CASE_TYPES } = await import("@/content/work-permit-corpus/wp-types");
    return { items: WP_CASE_TYPES.map((x) => ({ slug: x.slug, title: x.titleTh, desc: x.whoTh })) };
  },
  component: WpTypeHub,
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `ใบอนุญาตทำงานในไทย ${loaderData?.items.length ?? 0} กรณี — ขั้นตอน เอกสาร หน่วยงาน`,
        description:
          "รวมกรณีขอใบอนุญาตทำงานในไทย: บริษัททั่วไป (Non-B), BOI/One Stop Service, ครูต่างชาติ, สำนักงานผู้แทน, SMART/LTR, อาชีพที่ห้าม, การต่ออายุ/เปลี่ยนนายจ้าง และการรับรองเอกสารต่างประเทศ พร้อมแหล่งอ้างอิงราชการ",
        path: "/work-permit/type",
        alternates: [{ lang: "en", path: "/en/work-permit/type" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildSpeakableSchema("/work-permit/type")),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "ใบอนุญาตทำงาน", url: `${site}/work-permit/type` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: (loaderData?.items ?? []).map((t, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: t.title,
              url: `${site}/work-permit/type/${t.slug}`,
            })),
          }),
        },
      ],
    };
  },
});

function WpTypeHub() {
  const { items } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>ใบอนุญาตทำงาน</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ใบอนุญาตทำงานในไทย — {items.length} กรณีที่พบบ่อย
        </h1>
        <p className="mb-8 max-w-3xl text-muted-foreground">
          เลือกกรณีที่ตรงกับสถานะของคุณ แต่ละหน้าอธิบายลำดับขั้นตอน เอกสารที่ต้องใช้ หน่วยงานผู้พิจารณา
          และข้อผิดพลาดที่ทำให้เรื่องถูกตีกลับ ข้อมูลอ้างอิงจากประกาศและเว็บไซต์ของหน่วยงานราชการ
          การอนุมัติทุกกรณีเป็นดุลพินิจของพนักงานเจ้าหน้าที่
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((t: { slug: string; title: string; desc: string }) => (
            <a key={t.slug} href={`/work-permit/type/${t.slug}`} className="rounded-lg border bg-card p-5 transition hover:border-gold">
              <h2 className="mb-2 text-lg font-semibold">{t.title}</h2>
              <p className="text-sm text-muted-foreground">{t.desc}</p>
            </a>
          ))}
        </div>
        <section className="mt-10 rounded-lg border bg-muted/30 p-5">
          <h2 className="mb-2 text-xl font-bold">ต้องการให้ทีมงานตรวจเอกสารก่อนยื่น?</h2>
          <p className="text-sm text-muted-foreground">
            สอบถามรายละเอียดและขอบเขตงานได้ทางโทรศัพท์ LINE หรืออีเมล ทีมงานจะประเมินเอกสารตามกรณีของคุณ
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <p className="mt-8 text-xs text-muted-foreground">
          ข้อมูล ณ สิงหาคม 2569 (2026) โปรดตรวจสอบเงื่อนไขล่าสุดกับกรมการจัดหางานหรือสำนักงานตรวจคนเข้าเมืองก่อนดำเนินการ
        </p>
        <p className="mt-4 text-sm">
          อ่านคำถามที่พบบ่อย: <a href="/work-permit/faq" className="underline hover:text-gold">FAQ ใบอนุญาตทำงาน</a>
        </p>
      </main>
      <SiteFooter />
    </>
  );
}
